import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import {
  AccountCircle,
  Dashboard,
  AdminPanelSettings,
  Logout,
  Home,
  Policy,
  Description,
  WifiTethering,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';

const NavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const { isConnected, connectionStatus } = useWebSocket();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <AppBar position="static" elevation={1} sx={{ backgroundColor: '#2E86AB' }}>
      <Toolbar>
        {/* Logo/Title */}
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, fontWeight: 700, cursor: 'pointer' }}
          onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
        >
          🏥 2nd Opinion
        </Typography>

        {/* WebSocket Connection Status */}
        {isAuthenticated && (
          <Box sx={{ mr: 2 }}>
            <Chip
              icon={<WifiTethering />}
              label={connectionStatus}
              size="small"
              color={isConnected ? 'success' : 'default'}
              variant={isConnected ? 'filled' : 'outlined'}
              sx={{ 
                backgroundColor: isConnected ? '#4caf50' : 'transparent',
                color: isConnected ? 'white' : '#ccc',
                fontSize: '0.75rem'
              }}
            />
          </Box>
        )}

        {/* Navigation Links */}
        {!isAuthenticated ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="inherit"
              startIcon={<Home />}
              href="https://2ndopinionai.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Home
            </Button>
            <Button
              color="inherit"
              startIcon={<Policy />}
              onClick={() => navigate('/privacy')}
              sx={{ backgroundColor: isActive('/privacy') ? 'rgba(255,255,255,0.1)' : 'transparent' }}
            >
              Privacy
            </Button>
            <Button
              color="inherit"
              startIcon={<Description />}
              onClick={() => navigate('/terms')}
              sx={{ backgroundColor: isActive('/terms') ? 'rgba(255,255,255,0.1)' : 'transparent' }}
            >
              Terms
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Dashboard Button */}
            <Button
              color="inherit"
              startIcon={<Dashboard />}
              onClick={() => navigate('/dashboard')}
              sx={{ backgroundColor: isActive('/dashboard') ? 'rgba(255,255,255,0.1)' : 'transparent' }}
            >
              Dashboard
            </Button>

            {/* Admin Button (if admin) */}
            {user?.email === 'admin@2ndopinionai.org' && (
              <Button
                color="inherit"
                startIcon={<AdminPanelSettings />}
                onClick={() => navigate('/admin')}
                sx={{ backgroundColor: isActive('/admin') ? 'rgba(255,255,255,0.1)' : 'transparent' }}
              >
                Admin
              </Button>
            )}

            {/* User Menu */}
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              {user?.picture ? (
                <Avatar
                  src={user.picture}
                  alt={user.name}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user?.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {user?.email}
                  </Typography>
                </Box>
              </MenuItem>
              
              <MenuItem onClick={() => { handleClose(); navigate('/privacy'); }}>
                <Policy sx={{ mr: 1 }} /> Privacy Policy
              </MenuItem>
              
              <MenuItem onClick={() => { handleClose(); navigate('/terms'); }}>
                <Description sx={{ mr: 1 }} /> Terms of Service
              </MenuItem>
              
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;













